/**
 * Where a stored model PICK goes when its model leaves the picker, in ONE place.
 *
 * A person's pick lives in two stores: the browser (modelStore) and the account
 * row (user_preferences.default_model), which the server reads for every task it
 * starts itself (Composer, routines, continuations). The map used to live in the
 * web store alone, so the browser followed Opus 5 to Opus 5.5 while the account
 * row kept every server-started task on Opus 5. Both sides read this table now.
 *
 * SINGLE-HOP: `migratePick` maps once and never chains, so every entry names the
 * CURRENT successor directly (Opus 4.8 points at 5.5, not at 5). Only a stored
 * DEFAULT is ever migrated: an explicit per-task model and a task's recorded
 * model are the person's own choice and history, and bill at their own rate.
 *
 * A model REMOVED from the product is the registry's fact, not this table's: its
 * row names its successor (runnableModelId), and every id naming it runs that,
 * explicit picks included (Haiku 4.5 → Haiku 5.5, 2026-10-07).
 */
import { runnableModelId } from '../models/capabilities.js';

export const PICK_SUCCESSORS: Readonly<Record<string, string>> = {
  'claude-opus-4-8': 'claude-opus-5-5', // Opus 4.8 → Opus 5 (2026-07-24) → Opus 5.5 (2026-09-22)
  'claude-opus-5': 'claude-opus-5-5', // Opus 5 → Opus 5.5 (2026-09-22)
  'claude-fable-5': 'claude-fable-5-1', // Fable 5 → Fable 5.1 (2026-09-01)
  'gpt-5.6-sol': 'gpt-6.1-sol', // GPT-5.6 Sol → GPT-6 Sol (2026-09-23) → GPT-6.1 Sol (2026-09-30)
  'gpt-5.6-luna': 'gpt-6-luna', // GPT-5.6 Luna → GPT-6 Luna (2026-09-23)
  'gpt-5.6-terra': 'gpt-6.1-sol', // Terra retired 2026-09-23 for GPT-6 Sol, now GPT-6.1 Sol (2026-09-30)
  'gpt-6-sol': 'gpt-6.1-sol', // GPT-6 Sol → GPT-6.1 Sol (2026-09-30; the same card, cached input halved)
  'claude-sonnet-5': 'claude-sonnet-5-5', // Sonnet 5 → Sonnet 5.5 (2026-09-29; same price, reads 5's thinking)
  'grok-4.5': 'grok-4.7', // Grok 4.5 → Grok 4.7 (2026-09-29)
  'grok-4.6': 'grok-4.7', // Grok 4.6 → Grok 4.7 (2026-09-29; same card)
};

/** The successor of a delisted pick or a removed model, or the id unchanged. */
export function migratePick(id: string): string {
  return PICK_SUCCESSORS[id] ?? runnableModelId(id);
}

/**
 * The model a LATER turn of a task runs on, given the model its request carried and
 * the one the task recorded. The web pill is locked per task and echoes the task's
 * own model on every continuation, so a request model EQUAL to the recorded one is
 * the recording, not a new pick: it follows a retired model to its successor, as
 * resolveTaskContext does. Any other request model is the person's explicit choice
 * and rides verbatim, even a retired one, unless the product removed it.
 */
export function laterTurnModel(requested: string, recorded: string | null | undefined): string {
  return recorded && requested === recorded ? migratePick(requested) : runnableModelId(requested);
}
